import React, { useMemo } from "react";
import Select from "react-select";
import useIsMobile from "../../ui/useIsMobile.js";
import { Select as NativeSelect } from "./FormAtoms.jsx";
import { MobileSearchPickSingle } from "./MobileSearchPick.jsx";
import { getRsStyles } from "./SelectStyles.js";

function normalizeOptions(options = []) {
  return (options || []).map((o) => ({
    value: o?.value,
    label: String(o?.label ?? o?.value ?? ""),
  }));
}

function resolveSelectedOption(value, options) {
  if (value == null || value === "") return null;
  if (typeof value === "object" && "value" in value) return value;
  return options.find((o) => String(o.value) === String(value)) || null;
}

/**
 * Einzelauswahl: Mobile + isSearchable = Combobox (tippen & wählen), sonst natives OS-Picker.
 */
export default function ResponsiveSelect({
  options = [],
  value = null,
  onChange,
  isClearable = false,
  isSearchable = false,
  isDisabled = false,
  isLoading = false,
  placeholder = "Auswählen…",
  inputId,
  styles,
  menuPortalTarget,
  menuPosition = "fixed",
  noOptionsMessage,
  onBlur,
  "aria-invalid": ariaInvalid,
  classNamePrefix = "rs",
  mobileSearchPlaceholder = "Suchen und auswählen…",
  emptyMessage = "Keine Treffer",
}) {
  const isMobile = useIsMobile();
  const opts = useMemo(() => normalizeOptions(options), [options]);
  const selected = useMemo(() => resolveSelectedOption(value, opts), [value, opts]);

  const useMobileSearch = Boolean(isSearchable);

  if (!isMobile) {
    const rsStyles = styles || getRsStyles(true);
    return (
      <Select
        inputId={inputId}
        classNamePrefix={classNamePrefix}
        options={opts}
        value={selected}
        onChange={(opt) => onChange?.(opt || null)}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        placeholder={placeholder}
        styles={rsStyles}
        menuPosition={menuPosition}
        menuPortalTarget={menuPortalTarget}
        noOptionsMessage={
          typeof noOptionsMessage === "function"
            ? noOptionsMessage
            : () => (typeof noOptionsMessage === "string" ? noOptionsMessage : emptyMessage)
        }
        onBlur={onBlur}
        aria-invalid={ariaInvalid}
        components={{ IndicatorSeparator: () => null }}
      />
    );
  }

  if (useMobileSearch) {
    return (
      <MobileSearchPickSingle
        inputId={inputId}
        options={opts}
        value={selected}
        onChange={(opt) => onChange?.(opt || null)}
        placeholder={mobileSearchPlaceholder || placeholder}
        disabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        onBlur={onBlur}
        aria-invalid={ariaInvalid}
        emptyMessage={
          typeof noOptionsMessage === "string" ? noOptionsMessage : emptyMessage
        }
      />
    );
  }

  const nativeValue =
    selected?.value != null && selected?.value !== "" ? String(selected.value) : "";

  return (
    <div className="space-y-2 min-w-0">
      <NativeSelect
        id={inputId}
        value={nativeValue}
        onChange={(e) => {
          const picked = e.target.value;
          if (!picked) {
            onChange?.(null);
            return;
          }
          const opt = opts.find((o) => String(o.value) === picked);
          onChange?.(opt || null);
        }}
        disabled={isDisabled || isLoading}
        onBlur={onBlur}
        aria-invalid={ariaInvalid}
      >
        <option value="">{isLoading ? "Lädt…" : placeholder}</option>
        {opts.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {o.label}
          </option>
        ))}
      </NativeSelect>
      {!opts.length && !isLoading ? (
        <p className="text-xs text-blue-200/60">{emptyMessage}</p>
      ) : null}
      {isClearable && nativeValue ? (
        <button
          type="button"
          className="text-xs text-blue-300/80 hover:text-blue-100 min-h-[44px] md:min-h-0"
          onClick={() => onChange?.(null)}
          disabled={isDisabled}
        >
          Auswahl löschen
        </button>
      ) : null}
    </div>
  );
}
